import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  notificationTemplatesApi,
  type TemplateItem,
  type TemplateRowKey,
} from '@/api/notificationTemplates';
import { Button } from '@/components/ui/Button';
import { Card, CardTitle } from '@/components/ui/Card';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { Pill } from '@/components/ui/Pill';
import { apiErrorCode } from '@/lib/apiErrorCode';
import { formatLoginAt } from '@/lib/deviceLabels';
import {
  enableNeedsWarning,
  eventSwitch,
  isEmptyField,
  isStale,
  lintPlaceholders,
  unpublishNeedsConfirm,
} from '@/lib/notificationTemplates';

import { ApiErrorText, OkLine } from './notices';

/**
 * Карточка одной строки «событие × вариант × язык»: правка текста, галочка
 * «опубликовать», история версий, предпросмотр (D45, §4–§8 договора).
 */

const LABEL = 'mb-1.5 block text-xs font-semibold uppercase tracking-label text-on-surface-variant';
/**
 * ⚠️ ЗАБЛОКИРОВАННОЕ ПОЛЕ ОБЯЗАНО ВЫГЛЯДЕТЬ ЗАБЛОКИРОВАННЫМ. С одним
 * `disabled:opacity-60` пустое поле неотличимо от обычного пустого — на живом
 * экране 12.09.2026 «Заголовок пуша» выглядел как поле, которое просто забыли
 * заполнить, хотя оно обязано остаться пустым. Договор просит показывать такие
 * поля заблокированными, а не пустыми полями ввода.
 */
const AREA =
  'app-field w-full rounded-button bg-surface-container-lowest px-4 py-3 text-sm leading-relaxed text-on-surface shadow-soft focus:shadow-ambient focus:outline-none disabled:cursor-not-allowed disabled:bg-surface-container disabled:text-on-surface-variant disabled:shadow-none';

const FIELDS = ['title', 'body', 'push_title', 'push_body'] as const;
type Field = (typeof FIELDS)[number];

export function TemplateCard({ rowKey, onBack }: { rowKey: TemplateRowKey; onBack: () => void }) {
  const { t } = useTranslation();
  const item = useQuery({
    queryKey: ['admin', 'notification-templates', 'item', rowKey],
    queryFn: () => notificationTemplatesApi.item(rowKey),
  });

  return (
    <div className="space-y-3" data-template-card>
      <button
        type="button"
        data-template-back
        onClick={onBack}
        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
      >
        <span className="material-symbols-outlined text-[18px]">arrow_back</span>
        {t('adminNotificationTemplates.back')}
      </button>

      {item.isLoading ? <Card variant="subtle">{t('common.loading')}</Card> : null}
      {item.isError ? <ApiErrorText error={item.error} /> : null}
      {item.data ? (
        <Editor
          // Пересоздаём редактор при смене строки и версии: иначе в полях
          // остался бы текст прошлой.
          key={`${rowKey.kind}|${rowKey.key}|${rowKey.variant}|${rowKey.lang}|${item.data.current?.version ?? 0}`}
          rowKey={rowKey}
          item={item.data}
          onReread={() => void item.refetch()}
        />
      ) : null}
    </div>
  );
}

function Editor({
  rowKey,
  item,
  onReread,
}: {
  rowKey: TemplateRowKey;
  item: TemplateItem;
  onReread: () => void;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const cur = item.current;

  /**
   * ⚠️ У СТРОК ПИСЬМА ПОЛЕЙ СОБЫТИЯ НЕТ ВОВСЕ. `kind=email_texts` — это карта
   * `t_…` → строка, принадлежащая МАКЕТУ, а не событию. Показывать им
   * «Заголовок / Текст / пуш» значило бы предложить сохранить пустоту, на
   * которую сервер ответит `email_text_required`.
   */
  const isTexts = item.kind === 'email_texts';
  const textKeys = Object.keys(item.default_texts ?? {}).sort();

  const [draft, setDraft] = useState<Record<Field, string>>({
    title: cur?.title ?? item.default.title ?? '',
    body: cur?.body ?? item.default.body ?? '',
    push_title: cur?.push_title ?? item.default.push_title ?? '',
    push_body: cur?.push_body ?? item.default.push_body ?? '',
  });
  const [texts, setTexts] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const k of Object.keys(item.default_texts ?? {})) {
      out[k] = cur?.texts?.[k] ?? item.default_texts?.[k] ?? '';
    }
    return out;
  });
  const [error, setError] = useState<unknown>(null);
  const [ok, setOk] = useState('');
  // Ответ выключателя — свой и рядом с галочкой: общий итог стоит под полями, ниже экрана,
  // и щёлкнувший админ не видел ни «выключено», ни отказа.
  const [switchError, setSwitchError] = useState<unknown>(null);
  const [switchOk, setSwitchOk] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [preview, setPreview] = useState<null | Awaited<ReturnType<typeof notificationTemplatesApi.preview>>>(null);

  const lint = lintPlaceholders(item, isTexts ? texts : draft);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['admin', 'notification-templates'] });

  const save = useMutation({
    mutationFn: () =>
      notificationTemplatesApi.save(
        isTexts
          ? { ...rowKey, texts, version: cur?.version ?? 0 }
          : { ...rowKey, ...draft, version: cur?.version ?? 0 },
      ),
    onSuccess: (row) => {
      setError(null);
      setOk(t('adminNotificationTemplates.saved', { n: row.version }));
      invalidate();
      onReread();
    },
    onError: (e) => {
      setOk('');
      setError(e);
    },
  });

  const publish = useMutation({
    mutationFn: (next: boolean) =>
      notificationTemplatesApi.setPublished({ ...rowKey, published: next, version: cur?.version ?? 0 }),
    onSuccess: (row) => {
      setError(null);
      setOk(row.published ? t('adminNotificationTemplates.published') : t('adminNotificationTemplates.unpublished'));
      invalidate();
      onReread();
    },
    onError: (e) => {
      setOk('');
      setError(e);
    },
  });

  /**
   * Выключатель события целиком (§6а): все языки и варианты разом.
   *
   * ⚠️ `disabled` ШЛЁТСЯ ВСЕГДА ЯВНО. Сервер без него отвечает 400: `false` по умолчанию значил
   * бы «включить», и опечатка молча включила бы выключенное владельцем уведомление.
   * ⚠️ `version` — `switch_version`, а не версия текста: у выключателя своя история.
   */
  const sw = eventSwitch(item);
  const toggleSwitch = useMutation({
    mutationFn: (nextDisabled: boolean) =>
      notificationTemplatesApi.setDisabled({ key: item.key, disabled: nextDisabled, version: sw.version }),
    onSuccess: (reply) => {
      setSwitchError(null);
      setSwitchOk(reply.disabled ? t('adminNotificationTemplates.switchedOff') : t('adminNotificationTemplates.switchedOn'));
      invalidate();
      onReread();
    },
    onError: (e) => {
      setSwitchOk('');
      setSwitchError(e);
      // ⚠️ 409 ПЕРЕЧИТЫВАЕМ САМИ. У выключателя нет черновика, который перечтение затёрло бы, — в
      // отличие от полей текста. Без перечтения следующий щелчок снова уйдёт со старой версией.
      if (isStale(apiErrorCode(e))) {
        invalidate();
        onReread();
      }
    },
  });

  const onToggleSending = async () => {
    const nextDisabled = !sw.disabled;
    // Выключение — всегда с подтверждением: не уйдёт никому, ни в ленту, ни пушем, ни письмом.
    // Включение «скучаем» — с предупреждением: частоты нет, уйдёт каждый день.
    if (nextDisabled || enableNeedsWarning(item.key, nextDisabled)) {
      const okToGo = await confirm({
        title: nextDisabled ? t('adminNotificationTemplates.disableTitle') : t('adminNotificationTemplates.enableTitle'),
        message: nextDisabled
          ? t('adminNotificationTemplates.disableMessage', { key: item.key })
          : t('adminNotificationTemplates.enableInactiveWarning'),
        confirmLabel: nextDisabled
          ? t('adminNotificationTemplates.disableConfirm')
          : t('adminNotificationTemplates.enableConfirm'),
        danger: nextDisabled,
      });
      if (!okToGo) return;
    }
    toggleSwitch.mutate(nextDisabled);
  };

  const askPreview = useMutation({
    mutationFn: () =>
      notificationTemplatesApi.preview(isTexts ? { ...rowKey, texts } : { ...rowKey, ...draft }),
    onSuccess: (p) => {
      setError(null);
      setPreview(p);
    },
    onError: (e) => setError(e),
  });

  const history = useQuery({
    queryKey: ['admin', 'notification-templates', 'history', rowKey],
    queryFn: () => notificationTemplatesApi.history(rowKey),
    enabled: showHistory,
  });

  const onTogglePublished = async () => {
    const next = !cur?.published;
    if (unpublishNeedsConfirm(rowKey.lang, next)) {
      const okToGo = await confirm({
        title: t('adminNotificationTemplates.unpublishTitle'),
        message: t('adminNotificationTemplates.unpublishMessage', { lang: rowKey.lang.toUpperCase() }),
        confirmLabel: t('adminNotificationTemplates.unpublishConfirm'),
        danger: true,
      });
      if (!okToGo) return;
    }
    publish.mutate(next);
  };

  const code = apiErrorCode(error);
  const stale = isStale(code);

  return (
    <Card className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle>
          <span className="break-all font-mono text-base">{item.key}</span>
          {item.variant ? <span className="ml-2 text-sm text-on-surface-variant">{item.variant}</span> : null}
          <span className="ml-2 text-sm text-on-surface-variant">{rowKey.lang.toUpperCase()}</span>
        </CardTitle>
        {cur ? (
          <Pill variant={cur.published ? 'success' : 'neutral'}>
            {t('adminNotificationTemplates.currentVersion', { n: cur.version })}
          </Pill>
        ) : (
          <Pill>{t('adminNotificationTemplates.noVersion')}</Pill>
        )}
      </div>

      {/* ⚠️ Сказать прямо: у 25 шаблонов письмо — вёрстка в коде, и правка текста
          ленты его НЕ меняет. Без этой строки админ уверен, что поправил письмо. */}
      {!item.email_editable ? (
        <p className="rounded-xl bg-warning-container px-3 py-2 text-sm text-on-warning-container" data-template-email-note>
          {t('adminNotificationTemplates.emailFromCode')}
        </p>
      ) : null}

      {/* Выключатель события (§6а) — у событий, не у строк письма. Рисуем, только если сервер
          прислал поля: иначе на старом сервере переключатель выглядел бы «критичным» и
          недоступным, что неправда. */}
      {item.kind === 'template' && item.can_disable !== undefined ? (
        <div className="space-y-2 rounded-xl bg-surface-container-low p-3" data-template-switch>
          <label className="flex items-center gap-2 text-sm font-semibold text-on-surface">
            <input
              type="checkbox"
              data-template-sending
              checked={!sw.disabled}
              disabled={!sw.canDisable || toggleSwitch.isPending}
              onChange={() => void onToggleSending()}
            />
            {t('adminNotificationTemplates.sendingLabel')}
          </label>
          {!sw.canDisable ? (
            <p className="text-xs text-on-surface-variant" data-template-critical>
              {t('adminNotificationTemplates.sendingCriticalHint')}
            </p>
          ) : null}
          {sw.disabled ? (
            <p className="rounded-xl bg-warning-container px-3 py-2 text-sm text-on-warning-container" data-template-disabled-note>
              {t('adminNotificationTemplates.disabledNote')}
            </p>
          ) : null}
          <OkLine text={switchOk} />
          {switchError ? (
            isStale(apiErrorCode(switchError)) ? (
              <p
                role="alert"
                className="rounded-xl bg-error-container px-3 py-2 text-sm text-on-error-container"
                data-template-switch-stale
              >
                {t('adminNotificationTemplates.switchStale')}
              </p>
            ) : (
              <ApiErrorText error={switchError} />
            )
          ) : null}
        </div>
      ) : null}

      {isTexts ? (
        <>
          <p className="text-sm text-on-surface-variant">{t('adminNotificationTemplates.textsHint')}</p>
          {textKeys.map((k) => (
            <label key={k} className="block">
              <span className={LABEL}>{k}</span>
              <textarea
                data-template-text={k}
                rows={2}
                value={texts[k] ?? ''}
                onChange={(e) => setTexts((prev) => ({ ...prev, [k]: e.target.value }))}
                className={AREA}
              />
            </label>
          ))}
        </>
      ) : null}

      {!isTexts && FIELDS.map((field) => {
        const locked = isEmptyField(item, field);
        return (
          <label key={field} className="block">
            <span className={LABEL}>{t(`adminNotificationTemplates.field.${field}`)}</span>
            <textarea
              data-template-field={field}
              rows={field === 'body' ? 5 : 2}
              disabled={locked}
              value={locked ? '' : draft[field]}
              onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
              className={AREA}
            />
            {locked ? (
              <span className="mt-1 block text-xs text-on-surface-variant">
                {t('adminNotificationTemplates.fieldMustBeEmpty')}
              </span>
            ) : null}
          </label>
        );
      })}

      <div data-template-lint className="space-y-1 text-sm">
        {lint.missing.length ? (
          <p className="text-error">
            {t('adminNotificationTemplates.missing', {
              list: lint.missing.map((m) => `${m.field}: {{${m.name}}}`).join(', '),
            })}
          </p>
        ) : null}
        {lint.unknown.length ? (
          <p className="text-error">
            {t('adminNotificationTemplates.unknown', {
              list: lint.unknown.map((m) => `${m.field}: {{${m.name}}}`).join(', '),
            })}
          </p>
        ) : null}
        {!lint.missing.length && !lint.unknown.length ? (
          <p className="text-on-success-container">{t('adminNotificationTemplates.lintOk')}</p>
        ) : null}
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          data-template-published
          checked={!!cur?.published}
          disabled={publish.isPending || !cur}
          onChange={() => void onTogglePublished()}
        />
        {t('adminNotificationTemplates.publishedLabel')}
      </label>

      <div className="flex flex-wrap gap-2">
        <Button data-template-save loading={save.isPending} onClick={() => save.mutate()}>
          {t('adminNotificationTemplates.saveNext', { n: (cur?.version ?? 0) + 1 })}
        </Button>
        <Button
          variant="secondary"
          data-template-defaults
          onClick={() => {
            if (isTexts) {
              setTexts({ ...(item.default_texts ?? {}) });
            } else {
              setDraft({
                title: item.default.title ?? '',
                body: item.default.body ?? '',
                push_title: item.default.push_title ?? '',
                push_body: item.default.push_body ?? '',
              });
            }
            setOk(t('adminNotificationTemplates.defaultsFilled'));
          }}
        >
          {t('adminNotificationTemplates.defaults')}
        </Button>
        <Button variant="secondary" data-template-history aria-expanded={showHistory} onClick={() => setShowHistory((s) => !s)}>
          {t('adminNotificationTemplates.history')}
        </Button>
        <Button variant="secondary" data-template-preview loading={askPreview.isPending} onClick={() => askPreview.mutate()}>
          {t('adminNotificationTemplates.preview')}
        </Button>
      </div>

      <OkLine text={ok} />

      {error ? (
        <div className="space-y-2">
          <ApiErrorText error={error} />
          {/* ⚠️ ПОЛЯ НЕ ЗАТИРАЕМ. На 409 текст админа остаётся в полях, а
              «Перечитать» только подтягивает свежую версию — как у промптов ИИ. */}
          {stale ? (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span>{t('adminNotificationTemplates.staleHint')}</span>
              <Button data-template-reread variant="secondary" size="sm" onClick={onReread}>
                {t('adminNotificationTemplates.reread')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {preview ? <PreviewBlock preview={preview} /> : null}

      {showHistory ? (
        <div className="space-y-2 border-t border-outline/40 pt-3" data-template-history-list>
          {history.isLoading ? <p className="text-sm text-on-surface-variant">{t('common.loading')}</p> : null}
          {history.isError ? <ApiErrorText error={history.error} /> : null}
          {history.data?.versions.map((v) => (
            <div key={v.id} className="flex flex-wrap items-center gap-2 rounded-xl bg-surface-container-low p-3 text-sm">
              <span className="font-semibold">{`v${v.version}`}</span>
              <span className="text-xs text-on-surface-variant">{formatLoginAt(v.created_at, i18n.language)}</span>
              {v.archived_at ? (
                <Pill>{t('adminNotificationTemplates.archivedSince', { at: formatLoginAt(v.archived_at, i18n.language) })}</Pill>
              ) : (
                <Pill variant="success">{t('adminNotificationTemplates.active')}</Pill>
              )}
              <span className="min-w-0 flex-1 truncate text-on-surface-variant">{v.title || v.body || ''}</span>
              <Button
                variant="ghost"
                size="sm"
                data-template-take={v.id}
                onClick={() => {
                  setDraft({
                    title: v.title ?? '',
                    body: v.body ?? '',
                    push_title: v.push_title ?? '',
                    push_body: v.push_body ?? '',
                  });
                  setOk(t('adminNotificationTemplates.taken', { n: v.version }));
                }}
              >
                {t('adminNotificationTemplates.take')}
              </Button>
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

/**
 * Предпросмотр. Текст ленты и пуша — обычными узлами React.
 *
 * ⚠️ ПИСЬМО ПОКАЗЫВАЕТСЯ В ИЗОЛИРОВАННОЙ РАМКЕ, А НЕ ВСТАВКОЙ В СТРАНИЦУ.
 * Вставить чужую вёрстку через `dangerouslySetInnerHTML` нельзя правилом
 * дизайн-системы, а прогнать письмо через `sanitizeHtml` значило бы показать
 * НЕ ТО письмо, которое уйдёт человеку, — то есть соврать в предпросмотре.
 * `sandbox=""` запрещает и скрипты, и доступ к нашему origin.
 */
function PreviewBlock({ preview }: { preview: NonNullable<Awaited<ReturnType<typeof notificationTemplatesApi.preview>>> }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2 border-t border-outline/40 pt-3" data-template-preview-block>
      <p className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
        {t('adminNotificationTemplates.previewTitle')}
      </p>
      <div className="rounded-xl bg-surface-container-low p-3 text-sm">
        <p className="font-semibold text-on-surface">{preview.title}</p>
        <p className="whitespace-pre-wrap text-on-surface-variant">{preview.body}</p>
        {preview.push_title || preview.push_body ? (
          <p className="mt-2 text-xs text-on-surface-variant">
            {t('adminNotificationTemplates.previewPush', {
              title: preview.push_title ?? '',
              body: preview.push_body ?? '',
            })}
          </p>
        ) : null}
      </div>
      {preview.email_html ? (
        <>
          {preview.email_from_code ? (
            <p className="text-xs text-on-surface-variant">{t('adminNotificationTemplates.previewEmailFromCode')}</p>
          ) : null}
          <iframe
            data-template-preview-email
            title={t('adminNotificationTemplates.previewEmail')}
            sandbox=""
            srcDoc={preview.email_html}
            className="h-96 w-full rounded-xl border border-outline-variant/40 bg-white"
          />
        </>
      ) : null}
    </div>
  );
}
